import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSearchTerm } from '../redux/slices/appSlice';

// Cmd/Ctrl+F find-on-page overlay (desktop). Highlights every text match in
// the current view via the CSS Custom Highlight API and lets Enter/Shift+Enter
// (or the arrow buttons) jump between matches. Writes state.app.searchTerm,
// which is FIND-ONLY: tabs may scroll-to-match on it (Feed, Mixpedia, About)
// but must never hide rows or scope an action on it — table narrowing is the
// per-tab TabFilterBox (state.app.filterTerms) job.
const FindOverlay = () => {
    const searchTerm = useSelector((state) => state.app.searchTerm);
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [matchCount, setMatchCount] = useState(0);
    const inputRef = useRef(null);

    const close = useCallback(() => {
        dispatch(setSearchTerm(''));
        setOpen(false);
    }, [dispatch]);

    const openAndFocus = useCallback(() => {
        setOpen(true);
        setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 0);
    }, []);

    // Two open paths: the Edit → Find on Page menu item (its CmdOrCtrl+F
    // accelerator consumes the keystroke in the main process), plus a direct
    // keydown fallback for builds without the menu item.
    useEffect(() => {
        const handleKeydown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
                event.preventDefault();
                openAndFocus();
            }
        };
        window.addEventListener('keydown', handleKeydown);
        const offMenu = window.electron?.find?.onOpen ? window.electron.find.onOpen(openAndFocus) : undefined;
        return () => {
            window.removeEventListener('keydown', handleKeydown);
            if (offMenu) offMenu();
        };
    }, [openAndFocus]);

    useEffect(() => {
        if (!CSS.highlights) return;

        CSS.highlights.delete('search-results');
        setMatchCount(0);

        // Page-wide highlights only while the find bar itself is open.
        // (searchTerm is find-only now — tab filter boxes write their own
        // per-tab filterTerms — but the open-gate stays as a guard so a stale
        // term can never light up matches all over the page.)
        if (!open || !searchTerm) return;

        const root = document.querySelector('.app-container') || document.body;
        const term = searchTerm.toLowerCase();

        // Highlight ranges hold LIVE references to specific text nodes. If a
        // tab is still settling when we first paint (Crate Digger loading
        // cover art, lazy-resolving previews, DJPalScrollableList
        // re-rendering rows), React replaces those text nodes and the ranges
        // detach — the count (a plain number) survives but the highlight
        // paints nothing. So rebuild whenever the searched subtree mutates,
        // and once more on the next frame after this initial paint.
        const paint = () => {
            const treeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
            const ranges = [];
            let node;
            while ((node = treeWalker.nextNode())) {
                const text = node.textContent.toLowerCase();
                let startIdx = 0;
                while (startIdx < text.length) {
                    const idx = text.indexOf(term, startIdx);
                    if (idx === -1) break;
                    const range = new Range();
                    range.setStart(node, idx);
                    range.setEnd(node, idx + term.length);
                    ranges.push(range);
                    startIdx = idx + term.length;
                }
            }

            if (ranges.length > 0) {
                // eslint-disable-next-line no-undef
                CSS.highlights.set('search-results', new Highlight(...ranges));
            } else {
                CSS.highlights.delete('search-results');
            }
            setMatchCount(ranges.length);
        };

        paint();

        // Coalesce bursts of DOM mutations into a single rebuild per frame.
        // The Highlight API paints in a separate layer without touching the
        // DOM, so paint() never retriggers the observer — no loop.
        let raf = 0;
        const observer = new MutationObserver(() => {
            if (raf) return;
            raf = requestAnimationFrame(() => { raf = 0; paint(); });
        });
        observer.observe(root, { childList: true, subtree: true, characterData: true });

        return () => {
            observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [searchTerm, open]);

    const step = (direction) => {
        if (!searchTerm) return;
        window.dispatchEvent(new CustomEvent('search-next-match', { detail: direction }));
    };

    if (!open) return null;

    return (
        <div className="find-overlay">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="find-overlay-icon">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
                ref={inputRef}
                type="text"
                className="find-overlay-input"
                placeholder="Find on this page..."
                value={searchTerm}
                autoFocus
                onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        close();
                    }
                    if (e.key === 'Enter' && searchTerm) {
                        step(e.shiftKey ? -1 : 1);
                    }
                }}
            />
            {searchTerm && (
                <span className="find-overlay-count">
                    {matchCount === 0 ? 'No matches' : `${matchCount} match${matchCount === 1 ? '' : 'es'}`}
                </span>
            )}
            <button
                type="button"
                className="find-overlay-btn"
                aria-label="Previous match"
                title="Previous match (Shift+Enter)"
                disabled={!searchTerm || matchCount === 0}
                onClick={() => step(-1)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15" />
                </svg>
            </button>
            <button
                type="button"
                className="find-overlay-btn"
                aria-label="Next match"
                title="Next match (Enter)"
                disabled={!searchTerm || matchCount === 0}
                onClick={() => step(1)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            <button
                type="button"
                className="find-overlay-btn"
                aria-label="Close find bar"
                title="Close (Esc)"
                onClick={close}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
        </div>
    );
};

export default FindOverlay;
